import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, X } from 'lucide-react';

// Large pool of names - different combinations shown each day
const namePool = [
  { name: "Encik Razali", location: "Kuching", status: "Penjawat Awam" },
  { name: "Puan Siti", location: "Sibu", status: "Guru" },
  { name: "Sjn. Ahmad", location: "Miri", status: "Polis" },
  { name: "Cik Norhayati", location: "Bintulu", status: "Badan Berkanun" },
  { name: "Encik Hassan", location: "Sri Aman", status: "Penjawat Awam" },
  { name: "Cpt. Roslan", location: "Kapit", status: "Tentera" },
  { name: "Puan Aminah", location: "Mukah", status: "Guru" },
  { name: "Encik Zulkifli", location: "Limbang", status: "Penjawat Awam" },
  { name: "Cik Faridah", location: "Sarikei", status: "Badan Berkanun" },
  { name: "Sjn. Mazlan", location: "Lawas", status: "Polis" },
  { name: "Puan Zainab", location: "Kuching", status: "Guru" },
  { name: "Encik Bakri", location: "Miri", status: "Penjawat Awam" },
  { name: "Cik Salmah", location: "Sibu", status: "Badan Berkanun" },
  { name: "Sjn. Ibrahim", location: "Bintulu", status: "Polis" },
  { name: "Puan Halimah", location: "Kuching", status: "Guru" },
  { name: "Encik Ismail", location: "Sri Aman", status: "Penjawat Awam" },
  { name: "Cpt. Yusof", location: "Miri", status: "Tentera" },
  { name: "Cik Aisyah", location: "Sibu", status: "Guru" },
  { name: "Encik Rahman", location: "Bintulu", status: "Penjawat Awam" },
  { name: "Puan Rohana", location: "Mukah", status: "Badan Berkanun" },
  { name: "Sjn. Kamal", location: "Kapit", status: "Polis" },
  { name: "Encik Suhaimi", location: "Sarikei", status: "Penjawat Awam" },
  { name: "Puan Marlina", location: "Limbang", status: "Guru" },
  { name: "Cik Norsiah", location: "Kuching", status: "Badan Berkanun" },
  { name: "Cpt. Zaidi", location: "Miri", status: "Tentera" },
  { name: "Encik Fauzi", location: "Sibu", status: "Penjawat Awam" },
  { name: "Puan Rosnah", location: "Bintulu", status: "Guru" },
  { name: "Sjn. Anuar", location: "Kuching", status: "Polis" },
  { name: "Cik Zaharah", location: "Sri Aman", status: "Badan Berkanun" },
  { name: "Encik Yaakob", location: "Lawas", status: "Penjawat Awam" },
  { name: "Puan Norlela", location: "Miri", status: "Guru" },
  { name: "Sjn. Hafiz", location: "Sibu", status: "Polis" },
  { name: "Encik Kamaruddin", location: "Kuching", status: "Penjawat Awam" },
  { name: "Puan Junaidah", location: "Bintulu", status: "Badan Berkanun" },
  { name: "Cpt. Rizal", location: "Kapit", status: "Tentera" },
  { name: "Cik Latifah", location: "Sarikei", status: "Guru" },
  { name: "Encik Salleh", location: "Miri", status: "Penjawat Awam" },
  { name: "Puan Zaleha", location: "Kuching", status: "Guru" },
  { name: "Sjn. Rafiq", location: "Sibu", status: "Polis" },
  { name: "Encik Azman", location: "Mukah", status: "Penjawat Awam" }
];

// Simple deterministic random using day-of-year as seed
const seededShuffle = (array, seed) => {
  const shuffled = [...array];
  let currentSeed = seed;
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    // Simple LCG (Linear Congruential Generator)
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor((currentSeed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

// Get today's notifications - unique per day
const getTodayNotifications = () => {
  const today = new Date();
  // Day of year as seed (1-366)
  const start = new Date(today.getFullYear(), 0, 0);
  const diff = today - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  
  // Include year to make it unique across years
  const seed = today.getFullYear() * 1000 + dayOfYear;
  
  // Shuffle pool with today's seed and take first 10
  const shuffled = seededShuffle(namePool, seed);
  
  // Add random minutes (deterministic based on seed)
  return shuffled.slice(0, 10).map((person, idx) => {
    const minutesSeed = (seed + idx * 137) % 100;
    const minutes = 2 + Math.floor((minutesSeed / 100) * 45); // 2-47 minutes
    return { ...person, minutes };
  });
};

const LiveNotification = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  // Notifications shuffle daily based on date
  const notifications = useMemo(() => getTodayNotifications(), []);

  useEffect(() => {
    if (dismissed) return;
    const initialTimer = setTimeout(() => setIsVisible(true), 8000);
    return () => clearTimeout(initialTimer);
  }, [dismissed]);

  useEffect(() => {
    if (!isVisible || dismissed) return;
    const cycle = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
        setIsVisible(true);
      }, 4000);
    }, 12000);
    return () => clearInterval(cycle);
  }, [isVisible, dismissed, notifications.length]);

  const handleDismiss = () => {
    setDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible || dismissed) return null;

  const current = notifications[currentIndex];

  return (
    <div className="fixed bottom-24 left-4 sm:left-6 z-40 max-w-xs animate-slide-in-left">
      <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-green-500 p-4 pr-10 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-emerald-400 hover:text-emerald-700 transition-colors"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 relative">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md">
              {current.name.charAt(current.name.indexOf(' ') + 1) || current.name.charAt(0)}
            </div>
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
              <p className="text-xs text-green-700 font-bold uppercase tracking-wide">
                Sertai Baru
              </p>
            </div>
            <p className="text-sm font-bold text-emerald-900 leading-snug">
              {current.name} dari {current.location}
            </p>
            <p className="text-xs text-emerald-600 mt-0.5">
              {current.status}
            </p>
            <p className="text-xs text-emerald-500 mt-1.5 italic">
              ⏱️ {current.minutes} minit yang lalu
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-100 rounded-b-2xl overflow-hidden">
          <div className="h-full bg-green-500 animate-progress"></div>
        </div>
      </div>
    </div>
  );
};

export default LiveNotification;
